import React, { useEffect, useState } from 'react';
import {
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Badge,
  Tooltip
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

import { recupererDemandesBDC } from '../Services';
import InsuranceIndicator from './InsuranceIndicator';

interface DemandesDuBDCProps {
  idBonDeCommande: number;
}

interface Demande {
  demande_id: number;
  libelle_visa: string;
  prix_consulat: number;
  prix_intervention: number;
  assurancevisa: string;
  nom_voyageur: string;
  prenom_voyageur: string;
  options?: string; // ajout pour les options
  validite_visa?: string; // ajout pour la validité
}

const DemandesDuBDC: React.FC<DemandesDuBDCProps> = ({ idBonDeCommande }) => {
  const [demandes, setDemandes] = useState<Demande[]>([]);
  const [optionsDialogOpen, setOptionsDialogOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<string[] | null>(null);

  useEffect(() => {
    const fetchDemandes = async () => {
      try {
        const demandesDuBDC: Demande[] = await recupererDemandesBDC(idBonDeCommande);
        setDemandes(demandesDuBDC);
      } catch (error) {
        console.error('Erreur lors de la récupération des demandes pour le bon de commande:', error);
      }
    };

    fetchDemandes();
  }, [idBonDeCommande]);

  // Fonction pour ouvrir la fenêtre "Options"
  const handleOpenOptionsDialog = (optionsString: string | undefined) => {
    if (!optionsString) {
      setSelectedOptions([]);
    } else {
      // on split sur ";", et on retire les chaînes vides
      const splittedOptions = optionsString.split(';').filter(Boolean);
      setSelectedOptions(splittedOptions);
    }
    setOptionsDialogOpen(true);
  };

  const handleCloseOptionsDialog = () => {
    setOptionsDialogOpen(false);
    setSelectedOptions(null);
  };

  return (
    <Paper style={{ padding: 20 }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID Demande</TableCell>
              <TableCell>Libellé Visa</TableCell>
              <TableCell>Validité</TableCell>
              <TableCell>Prix Consulat</TableCell>
              <TableCell>Prix Intervention</TableCell>
              <TableCell>Options</TableCell>
              <TableCell>Assurance</TableCell>
              <TableCell>Nom Voyageur</TableCell>
              <TableCell>Prénom Voyageur</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {demandes.map((demande) => {
              // Contrôle si le libellé contient "evisa" (en ignorant la casse)
              const isEvisa = demande.libelle_visa.toLowerCase().includes('evisa');
              // Préparer la liste d’options potentielle
              // (on compte le nombre d’options non vides pour l’afficher dans le badge)
              const optionsArray = demande.options
                ? demande.options.split(';').filter(Boolean)
                : [];
              
              return (
                <TableRow key={demande.demande_id}>
                  <TableCell>{demande.demande_id}</TableCell>
                  <TableCell>{demande.libelle_visa}</TableCell>
                  <TableCell>
                    {demande.validite_visa && demande.validite_visa.length > 20 ? (
                      <Tooltip title={demande.validite_visa} arrow>
                        <span style={{
                          display: 'inline-block',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {demande.validite_visa}
                        </span>
                      </Tooltip>
                    ) : (
                      <span>{demande.validite_visa || '-'}</span>
                    )}
                  </TableCell>
                  <TableCell>{demande.prix_consulat}€</TableCell>
                  <TableCell>{demande.prix_intervention}€</TableCell>

                  {/* N'affiche le bouton Options que si le visa n'est PAS un eVisa */}
                  <TableCell>
                    {!isEvisa && (
                      <IconButton onClick={() => handleOpenOptionsDialog(demande.options)}>
                        <Badge
                          badgeContent={optionsArray.length}
                          color={optionsArray.length > 0 ? 'primary' : 'default'}
                        >
                          <InfoIcon />
                        </Badge>
                      </IconButton>
                    )}
                  </TableCell>

<TableCell>
  <InsuranceIndicator assurancevisa={demande.assurancevisa} />
</TableCell>
                  <TableCell>{demande.nom_voyageur}</TableCell>
                  <TableCell>{demande.prenom_voyageur}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour afficher la liste des options */}
      <Dialog open={optionsDialogOpen} onClose={handleCloseOptionsDialog}>
        <DialogTitle>Options</DialogTitle>
        <DialogContent>
          {selectedOptions && selectedOptions.length > 0 ? (
            <ul>
              {selectedOptions.map((opt, index) => (
                <li key={index}>{opt}</li>
              ))}
            </ul>
          ) : (
            <p>Aucune option</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOptionsDialog}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default DemandesDuBDC;
